/**
 * Call Config Tab Component
 *
 * Rendered inside the Settings dialog. Lets the current user edit
 * their own extension (SIP) and mobile number.
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, type KeyboardEvent } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

import { useAuthStore } from '@/modules/auth/application/store';
import { getCurrentUser } from '@/modules/user';
import { useUserById, useUpdateUser } from '../../application';
import { createZodResolver, useServerFormErrors, phoneSchema } from '@/shared/utils/validation';
import { Form, useToast } from '@/shared/components';

// ============================================================================
// Types
// ============================================================================

interface AvailableSip {
  id: number | string;
  extension: string | number;
  sip_type?: string;
  [key: string]: unknown;
}

// ============================================================================
// Validation Schema
// ============================================================================

const callConfigSchema = z.object({
  mobile_number: phoneSchema,
  extension: z.string().min(1, 'Extension is required'),
});

type CallConfigFormData = z.infer<typeof callConfigSchema>;

const API_TO_FORM_FIELD_MAP: Record<string, keyof CallConfigFormData> = {
  mobile_number: 'mobile_number',
  mobileNumber: 'mobile_number',
  extension: 'extension',
};

// ============================================================================
// Component
// ============================================================================

interface CallConfigTabProps {
  onSaved?: () => void;
}

export function CallConfigTab({ onSaved }: CallConfigTabProps) {
  const toast = useToast();
  const authUser = useAuthStore(state => state.user);
  const setAuthUser = useAuthStore(state => state.setUser);

  const userId = authUser?.id ?? null;

  const { data: user, isLoading: isLoadingUser } = useUserById(userId);
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUser();

  // Extract availableSips from user (may be camelCase or snake_case)
  const availableSips = useMemo(() => {
    if (!user) return [];
    const sips =
      (Array.isArray(user['availableSips']) ? user['availableSips'] : null) ??
      (Array.isArray(user['available_sips']) ? user['available_sips'] : null) ??
      [];
    return sips as AvailableSip[];
  }, [user]);

  // Get current SIP id based on user's extension
  const currentSipId = useMemo(() => {
    if (!user || availableSips.length === 0) return '';

    const rawSipExt = user['sip_extension'];
    const rawSipExtAlt = user['sipExtension'];
    const sipExtension = typeof rawSipExt === 'string' || typeof rawSipExt === 'number' ? rawSipExt : null;
    const sipExtensionAlt = typeof rawSipExtAlt === 'string' || typeof rawSipExtAlt === 'number' ? rawSipExtAlt : null;
    const userExtension = sipExtension ?? sipExtensionAlt ?? user.extension ?? null;

    if (userExtension != null) {
      const matchingSip = availableSips.find(sip => String(sip.extension) === String(userExtension));
      if (matchingSip) return String(matchingSip.id);
    }

    const defaultSip = availableSips.find(sip => sip['is_default'] === true || sip['default'] === true);
    if (defaultSip) return String(defaultSip.id);

    return '';
  }, [user, availableSips]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    setError,
  } = useForm<CallConfigFormData>({
    resolver: createZodResolver(callConfigSchema),
    defaultValues: {
      mobile_number: '',
      extension: '',
    },
  });

  // Reset form when user data loads / changes
  useEffect(() => {
    if (user) {
      reset({
        mobile_number:
          (typeof user['mobile_number'] === 'string' ? user['mobile_number'] : user.mobileNumber) ?? '',
        extension: currentSipId,
      });
    }
  }, [user, currentSipId, reset]);

  // Update extension when currentSipId becomes available after initial load
  useEffect(() => {
    if (currentSipId && user) {
      setValue('extension', currentSipId);
    }
  }, [currentSipId, user, setValue]);

  const handleServerErrors = useServerFormErrors(API_TO_FORM_FIELD_MAP, 'Failed to update call config');

  const onSubmit = async (data: CallConfigFormData) => {
    if (!user) return;

    const updateRequest = {
      authenticated_id: user.authenticatedId ?? user.id,
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      email: user.email ?? '',
      mobile_number: data.mobile_number,
      extension: Number(data.extension),
      is_active: user.is_active !== undefined ? user.is_active : true,
    };

    return new Promise<void>((resolve, reject) => {
      updateUser(updateRequest, {
        onSuccess: () => {
          // Refresh auth store so sidebar / SIP registration picks up new values
          getCurrentUser()
            .then(({ user: refreshedUser }) => setAuthUser(refreshedUser))
            .catch(err => console.error('Failed to refresh current user after call config update:', err));
          toast.success('Call config saved successfully');
          onSaved?.();
          resolve();
        },
        onError: error => {
          reject(error);
        },
      });
    });
  };

  const handleFormSubmit = async (data: CallConfigFormData) => {
    try {
      await onSubmit(data);
    } catch (error) {
      handleServerErrors(error, setError);
    }
  };

  if (isLoadingUser) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)} disabled={isUpdating} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Mobile Number
        </Typography>
        <TextField
          {...register('mobile_number')}
          fullWidth
          size="small"
          placeholder="Format: 65192301284"
          error={!!errors.mobile_number}
          helperText={errors.mobile_number?.message}
          disabled={isUpdating}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          onKeyDown={(e: KeyboardEvent) => { if (!/[\d\b]/.test(e.key) && !['ArrowLeft','ArrowRight','Delete','Backspace','Tab'].includes(e.key) && !e.metaKey && !e.ctrlKey) e.preventDefault(); }}
        />
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Extension
        </Typography>
        <Controller
          name="extension"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size="small" error={!!errors.extension}>
              <Select {...field} displayEmpty disabled={isUpdating || availableSips.length === 0}>
                <MenuItem value="" disabled>
                  <Typography color="text.disabled">
                    {availableSips.length === 0 ? 'No available extensions' : 'Select extension...'}
                  </Typography>
                </MenuItem>
                {availableSips.map(sip => {
                  const sipId = String(sip.id);
                  const extensionNum = String(sip.extension);
                  const sipType = sip.sip_type ? String(sip.sip_type).toUpperCase() : '';
                  const label = sipType ? `${extensionNum} - ${sipType}` : extensionNum;

                  return (
                    <MenuItem key={sipId} value={sipId}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
              {errors.extension ? (
                <FormHelperText>{errors.extension.message}</FormHelperText>
              ) : availableSips.length === 0 ? (
                <FormHelperText>No available Extension for this User</FormHelperText>
              ) : null}
            </FormControl>
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
        <Button type="submit" variant="contained" disabled={isUpdating}>
          {isUpdating ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Form>
  );
}
